import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard3D = ({ project, index, onProjectSelect, isActive }) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef?.current) return;
    
    const rect = cardRef?.current?.getBoundingClientRect();
    const x = e?.clientX - rect?.left;
    const y = e?.clientY - rect?.top;
    
    const centerX = rect?.width / 2;
    const centerY = rect?.height / 2;
    
    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;
    
    cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(20px)`;
  };

  const handleMouseLeave = () => {
    if (cardRef?.current) {
      cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
    }
    setIsHovered(false);
  };

  return (
    <motion.div
      ref={cardRef}
      className={`relative group cursor-pointer transition-all duration-500 ${
        isActive ? 'z-20 scale-105' : 'z-10'
      }`}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={() => onProjectSelect(project)}
    >
      {/* 3D Card Container */}
      <div className="relative w-full h-96 glass-panel rounded-xl overflow-hidden cosmic-glow-hover transform-3d">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src={project?.image}
            alt={project?.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent"></div>
        </div>

        {/* Floating Category Badge */}
        <div className="absolute top-4 left-4 z-10">
          <div className={`px-3 py-1 rounded-full text-xs font-accent font-medium backdrop-blur-sm border ${
            project?.category === 'Web Development' ?'bg-accent/20 text-accent border-accent/30'
              : project?.category === 'Creative Coding' ?'bg-cosmic-energy/20 text-cosmic-energy border-cosmic-energy/30' :'bg-cosmic-cta/20 text-cosmic-cta border-cosmic-cta/30'
          }`}>
            {project?.category}
          </div>
        </div>

        {/* Tech Stack Icons */}
        <div className="absolute top-4 right-4 flex space-x-2">
          {project?.technologies?.slice(0, 3)?.map((tech, idx) => (
            <div
              key={idx}
              className="w-8 h-8 glass-panel-light rounded-lg flex items-center justify-center"
              title={tech}
            >
              <Icon 
                name={tech === 'React' ? 'Zap' : tech === 'Node.js' ? 'Server' : 'Code'} 
                size={16} 
                className="text-accent" 
              />
            </div>
          ))}
        </div>

        {/* Content Area */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="space-y-3">
            <h3 className="font-headline font-bold text-xl text-foreground group-hover:text-accent transition-colors">
              {project?.title}
            </h3>
            
            <p className="font-body text-sm text-muted-foreground line-clamp-2">
              {project?.description}
            </p>

            {/* Project Stats */}
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Calendar" size={12} />
                <span>{project?.year}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={12} />
                <span>{project?.duration}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Star" size={12} />
                <span>{project?.rating}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="border-accent/30 text-accent hover:bg-accent/10"
                iconName="ExternalLink"
                iconPosition="right"
                iconSize={14}
              >
                View Live
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                iconName="Github"
                iconSize={14}
              >
                Code
              </Button>
            </div>
          </div>
        </div>

        {/* Hover Overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-accent/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          initial={false}
          animate={{ opacity: isHovered ? 1 : 0 }}
        />

        {/* 3D Depth Indicators */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-2 left-2 w-2 h-2 bg-accent/30 rounded-full animate-pulse"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 bg-cosmic-energy/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </div>
      {/* Floating Info Panel (Appears on Hover) */}
      <motion.div
        className="absolute -right-4 top-1/2 transform -translate-y-1/2 w-64 glass-panel rounded-lg p-4 shadow-cosmic-lg"
        initial={{ opacity: 0, x: -20, scale: 0.9 }}
        animate={{ 
          opacity: isHovered ? 1 : 0, 
          x: isHovered ? 0 : -20,
          scale: isHovered ? 1 : 0.9
        }}
        transition={{ duration: 0.3 }}
        style={{ pointerEvents: isHovered ? 'auto' : 'none' }}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-headline font-bold text-sm text-foreground">Project Details</h4>
            <Icon name="Info" size={16} className="text-accent" />
          </div>
          
          <div className="space-y-2 text-xs">
            <div>
              <span className="text-muted-foreground">Client:</span>
              <span className="text-foreground ml-2">{project?.client}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Role:</span>
              <span className="text-foreground ml-2">{project?.role}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Impact:</span>
              <span className="text-accent ml-2">{project?.impact}</span>
            </div>
          </div>

          <div className="pt-2 border-t border-border/20">
            <div className="flex flex-wrap gap-1">
              {project?.technologies?.map((tech, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-surface/50 text-muted-foreground rounded text-xs"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProjectCard3D;